/**
 * L'ÉCHÉANCIER — LA DATE PRÉVUE D'UNE PIÈCE (lot 97, 18/09/2026).
 *
 * Cadrage : « Analyse financière : 2. l'échéancier » et « 8. les arbitrages »
 * (17/09/2026). Ce fichier est la SEULE source des dates prévues : le BFR et
 * la trésorerie les liront d'ici (doc 8, « une seule source de dates
 * prévues »). Il ne touche ni à la base ni au réseau.
 *
 * LES MONTANTS SONT EN CENTIMES ENTIERS. Un découpage en trois paiements doit
 * retomber exactement sur le reste dû ; en euros flottants, il ne retombe pas
 * toujours.
 *
 * LES RÈGLES, dans l'ordre où elles s'appliquent :
 *
 *   1. un PRÉLÈVEMENT tombe à son échéance, et n'est pas modifiable ;
 *   2. des PAIEMENTS SAISIS (un ou plusieurs, « montant + date ») font foi ;
 *   3. sinon la date est CALCULÉE : échéance + retard habituel du tiers
 *      (médiane de ses règlements, jamais négative — maquette v2) ;
 *   4. une date passée d'une pièce non réglée est ramenée à AUJOURD'HUI et
 *      marquée « à revoir » (doc 8 : « date dépassée → semaine en cours »).
 */

/** Un paiement saisi. `montant` nul : tout le reste dû, à cette date. */
export interface TrancheSaisie {
  readonly montant_centimes: number | null;
  readonly date: string;
}

export interface PieceAPrevoir {
  readonly echeance: string | null;
  readonly date_piece: string | null;
  readonly reste_centimes: number;
  readonly prelevement: boolean;
  /** Retard habituel du tiers, en jours. Nul : pas d'historique. */
  readonly retard_jours: number | null;
  readonly tranches: readonly TrancheSaisie[] | null;
}

export type OrigineDate = 'saisie' | 'echeancier' | 'calculee' | 'prelevement';

/** Une part de pièce, à une date. Une pièce non découpée n'en a qu'une. */
export interface Part {
  readonly montant_centimes: number;
  /** La date retenue — jamais avant aujourd'hui. */
  readonly date: string;
  /** La date saisie ou calculée, avant le report à aujourd'hui. */
  readonly date_brute: string;
  readonly origine: OrigineDate;
  readonly a_revoir: boolean;
  /** Rang de la part (0…) et nombre de parts, pour « 2/3 ». */
  readonly rang: number;
  readonly nombre: number;
}

const JOUR_MS = 86_400_000;

function versMs(iso: string): number {
  const [a, m, j] = iso.split('-').map(Number);
  return Date.UTC(a as number, (m as number) - 1, j as number);
}

function depuisMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** `iso` + `n` jours, en dates civiles (pas d'heure d'été ici). */
export function ajouterJours(iso: string, n: number): string {
  return depuisMs(versMs(iso) + n * JOUR_MS);
}

/** Nombre de jours de `a` à `b` (positif si `b` est après). */
export function ecartJours(a: string, b: string): number {
  return Math.round((versMs(b) - versMs(a)) / JOUR_MS);
}

/** Le lundi de la semaine d'une date. */
export function lundiDe(iso: string): string {
  const jourSemaine = (new Date(versMs(iso)).getUTCDay() + 6) % 7;
  return ajouterJours(iso, -jourSemaine);
}

/** Le numéro de semaine ISO 8601 (1…53). */
export function semaineIso(iso: string): number {
  const d = new Date(versMs(iso));
  const jeudi = versMs(ajouterJours(iso, 3 - ((d.getUTCDay() + 6) % 7)));
  const annee = new Date(jeudi).getUTCFullYear();
  const premierJeudi = versMs(`${String(annee)}-01-04`);
  const lundiPremier = versMs(lundiDe(depuisMs(premierJeudi)));
  return 1 + Math.floor((jeudi - lundiPremier) / (7 * JOUR_MS));
}

/** La médiane, ou nul pour une liste vide. */
export function mediane(valeurs: readonly number[]): number | null {
  if (valeurs.length === 0) return null;
  const t = [...valeurs].sort((a, b) => a - b);
  const milieu = Math.floor(t.length / 2);
  return t.length % 2 === 1
    ? (t[milieu] as number)
    : ((t[milieu - 1] as number) + (t[milieu] as number)) / 2;
}

/** Un règlement passé d'un tiers. */
export interface ReglementPasse {
  readonly date_piece: string | null;
  readonly echeance: string | null;
  readonly regle_le: string | null;
}

export interface RetardHabituel {
  /** Médiane arrondie, en jours ; nul sans historique. Peut être négative. */
  readonly jours: number | null;
  /** Règlements retenus. */
  readonly retenus: number;
  /** Règlements écartés : dates manquantes ou incohérentes (doc 8). */
  readonly exclus: number;
}

/**
 * LE RETARD HABITUEL D'UN TIERS : la MÉDIANE de (date de règlement −
 * échéance). Une pièce réglée avant d'être émise est une date incohérente :
 * elle est écartée et comptée, comme le demande le doc 8.
 */
export function retardHabituel(reglements: readonly ReglementPasse[]): RetardHabituel {
  const ecarts: number[] = [];
  let exclus = 0;
  for (const r of reglements) {
    if (r.regle_le === null || r.echeance === null) {
      exclus += 1;
      continue;
    }
    if (r.date_piece !== null && r.regle_le < r.date_piece) {
      exclus += 1;
      continue;
    }
    ecarts.push(ecartJours(r.echeance, r.regle_le));
  }
  const m = mediane(ecarts);
  return { jours: m === null ? null : Math.round(m), retenus: ecarts.length, exclus };
}

/** La base d'une pièce : son échéance, ou à défaut sa date. */
function base(p: PieceAPrevoir, aujourdhui: string): string {
  return p.echeance ?? p.date_piece ?? aujourdhui;
}

/**
 * LA DATE CALCULÉE : L'ÉCHÉANCE, ET RIEN D'AUTRE (lot 110, 21/09/2026).
 *
 * Elle valait « échéance + retard habituel du tiers ». Décision de
 * l'exploitation du 21/09/2026 : une facture à venir est prévue à sa date
 * d'échéance, point. Le retard habituel était une prévision de mauvaise
 * conduite appliquée d'office, y compris à un tiers qui n'a encore rien fait.
 *
 * LA VUE PESSIMISTE N'EST PAS PERDUE : le scénario PRUDENT de la trésorerie
 * applique le PIRE retard observé du tiers, et seulement aux dates calculées
 * (`tresorerie.ts`, `prudentDe`). Les deux lectures restent donc disponibles —
 * l'une à l'échéance, l'autre au pire.
 */
export function dateCalculee(p: PieceAPrevoir, aujourdhui: string): string {
  return base(p, aujourdhui);
}

function reporter(
  date: string,
  aujourdhui: string,
): { date: string; a_revoir: boolean } {
  return date < aujourdhui
    ? { date: aujourdhui, a_revoir: true }
    : { date, a_revoir: false };
}

/**
 * LES PARTS D'UNE PIÈCE, chacune à sa date.
 *
 * DES PAIEMENTS SAISIS QUI NE RETOMBENT PLUS SUR LE RESTE DÛ — un règlement
 * partiel arrivé depuis — sont ajustés ainsi : ce qui a été réglé est retiré
 * des PREMIERS paiements (on règle d'abord ce qui vient d'abord) ; un reste
 * plus grand que prévu s'ajoute au DERNIER. Dans les deux cas la pièce est
 * marquée « à revoir » : le découpage ne correspond plus à ce qui a été saisi.
 */
export function partsDe(p: PieceAPrevoir, aujourdhui: string): readonly Part[] {
  if (p.reste_centimes <= 0) return [];

  if (p.prelevement) {
    const brute = base(p, aujourdhui);
    return [
      {
        montant_centimes: p.reste_centimes,
        ...reporter(brute, aujourdhui),
        date_brute: brute,
        origine: 'prelevement',
        rang: 0,
        nombre: 1,
      },
    ];
  }

  const saisies = p.tranches ?? [];
  if (saisies.length === 1 && (saisies[0] as TrancheSaisie).montant_centimes === null) {
    const brute = (saisies[0] as TrancheSaisie).date;
    return [
      {
        montant_centimes: p.reste_centimes,
        ...reporter(brute, aujourdhui),
        date_brute: brute,
        origine: 'saisie',
        rang: 0,
        nombre: 1,
      },
    ];
  }

  if (saisies.length > 0) {
    const triees = [...saisies].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
    );
    const montants = triees.map((t) => Math.max(0, t.montant_centimes ?? 0));
    const prevu = montants.reduce((s, m) => s + m, 0);
    let ecart = prevu - p.reste_centimes;
    const ajuste = ecart !== 0;
    if (ecart > 0) {
      for (let i = 0; i < montants.length && ecart > 0; i += 1) {
        const retire = Math.min(montants[i] as number, ecart);
        montants[i] = (montants[i] as number) - retire;
        ecart -= retire;
      }
    } else if (ecart < 0) {
      montants[montants.length - 1] = (montants[montants.length - 1] as number) - ecart;
    }
    const nombre = triees.length;
    const parts: Part[] = [];
    triees.forEach((t, rang) => {
      const m = montants[rang] as number;
      if (m <= 0) return;
      const r = reporter(t.date, aujourdhui);
      parts.push({
        montant_centimes: m,
        date: r.date,
        a_revoir: r.a_revoir || ajuste,
        date_brute: t.date,
        origine: 'echeancier',
        rang,
        nombre,
      });
    });
    return parts;
  }

  /**
   * UNE PIÈCE DÉJÀ ÉCHUE NE REÇOIT AUCUNE DATE (lot 110, 21/09/2026).
   *
   * Avant, sa date était ramenée à aujourd'hui et marquée « à revoir » : le
   * calcul affirmait qu'elle rentrait le jour même, ce qui était la plus
   * optimiste des hypothèses appliquée aux pièces les plus douteuses.
   *
   * Décision de l'exploitation : on n'invente plus de date. Elle est datée à
   * la main, ou elle n'est prévue nulle part.
   *
   * CE QUE CELA NE VEUT PAS DIRE : que la pièce disparaît. Elle reste due,
   * donc le BFR continue de la compter à son reste dû tant qu'elle n'est pas
   * réglée — `resteA` (bfr.ts) traite à part une pièce ouverte sans part.
   * Sans cette contrepartie, « aucune date » aurait reproduit exactement le
   * défaut qu'on venait de corriger.
   */
  const brute = base(p, aujourdhui);
  if (brute < aujourdhui) return [];
  return [
    {
      montant_centimes: p.reste_centimes,
      date: brute,
      a_revoir: false,
      date_brute: brute,
      origine: 'calculee',
      rang: 0,
      nombre: 1,
    },
  ];
}

/**
 * CONTRÔLE D'UN DÉCOUPAGE avant enregistrement : le total doit égaler le
 * reste dû (doc 8). Rend l'écart en centimes (positif : il manque).
 */
export function ecartDuDecoupage(
  tranches: readonly { readonly montant_centimes: number }[],
  reste_centimes: number,
): number {
  return reste_centimes - tranches.reduce((s, t) => s + t.montant_centimes, 0);
}

/** Des euros en texte (« 1200.50 ») vers des centimes, sans flottant piège. */
export function centimes(montant: string | number | null | undefined): number {
  if (montant === null || montant === undefined || montant === '') return 0;
  const n = typeof montant === 'number' ? montant : Number(montant);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
